<template>
  <div>
    <CautionDialog :display="isDisplayCautionDialog" />
    <PageHeaderBanner>
      <template #title>新規会員登録</template>
    </PageHeaderBanner>
    <div class="mx-auto cmr00100-form-area">
      <MessageChip
        :display="isMessageChip"
        level="E"
        :message="errMessage"
        style="margin-top: -15px"
      />
      <InputFormHeader :list="titleList" :page="page" />
      <q-form
        autofocus
        greedy
        novalidate
        @submit="page === 0 ? checkUserInfo() : (page += 1)"
        @validation-error="resetError"
      >
        <div v-if="page === 0">
          <!-- ユーザー情報 -->
          <UserInfoForm
            :member-info="inputData"
            :is-mail-address-err="isMailAddressErr"
            :mode="DisplayModeCode.REGISTER"
            @is-mail-address-err="isMailAddressErr = $event"
          />
        </div>
        <div v-if="page === 1">
          <!-- 所属先情報 -->
          <AfiliationInfoForm :member-info="inputData" />
        </div>
        <div v-if="page === 2">
          <!-- 登録確認 -->
          <ConfirmationForm :member-info="inputData" />
          <!-- 利用規約・プライバシーポリシー -->
          <ServiceAgreeForm
            class="q-mt-lg"
            :is-agree-checked="isAgreeChecked"
            @terms="isAgreeChecked.terms = $event"
            @policy="isAgreeChecked.policy = $event"
          />
        </div>
        <FormFooterBanner style="position: inherit">
          <template #button-area>
            <div>
              <q-btn
                v-if="page === 1 || page === 2"
                color="primary"
                outline
                style="width: 120px"
                class="q-mr-xs"
                icon="mdi-chevron-left"
                label="前へ"
                @click="page -= 1"
              />
              <!-- ユーザー情報「次へ」 -->
              <q-btn
                v-if="page === 0"
                type="submit"
                color="primary"
                outline
                style="width: 120px"
                icon-right="mdi-chevron-right"
                label="次へ"
              />
              <!-- 所属先情報「次へ」 -->
              <q-btn
                v-if="page === 1"
                type="submit"
                color="primary"
                outline
                style="width: 120px"
                icon-right="mdi-chevron-right"
                label="次へ"
              />
              <!-- 登録ボタン -->
              <span>
                <q-btn
                  v-if="page === 2"
                  color="primary"
                  :disabled="isAgreeChecked.terms && isAgreeChecked.policy ? false : true"
                  style="width: 120px"
                  label="登録"
                  @click="registerMember"
                >
                </q-btn>
                <q-tooltip
                  v-if="isAgreeChecked.terms && isAgreeChecked.policy ? false : true"
                  anchor="top middle"
                  self="bottom middle"
                  :offset="[15, 15]"
                >
                  <strong>会員登録には利用規約・プライバシーポリシーの同意が必要です。</strong>
                </q-tooltip>
              </span>
            </div>
          </template>
        </FormFooterBanner>
      </q-form>
      <AlertDialog
        :display="isAlertFlg"
        level="information"
        confirmation
        title="新規会員登録の受付が完了致しました"
        :text="[
          'ご登録ありがとうございます。',
          '担当者より折り返しご連絡させていただきますので、今しばらくお待ちくださいますようお願い申し上げます。',
          '2営業日を過ぎても返信がない場合は、メールの送受信に不具合がある可能性がございますので、お手数ですが、お電話にて下記までお問合せください。',
          '',
          'TEL：03-6262-6467',
        ]"
        @complete="router.push({ name: 'login' })"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';
import { Loading } from 'quasar';
import axios from '../../common/axios-common';
import * as UserAttributeCode from '../../common/code/UserAttributeCode';
import DisplayModeCode from '../../common/code/DisplayModeCode';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import FormFooterBanner from '../../components/banner/FormFooterBanner.vue';
import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import MessageChip from '../../components/chip/MessageChip.vue';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import CautionDialog from '../../components/dialogs/CautionDialog.vue';
import AfiliationInfoForm from '../../components/form/member/AfiliationInfoForm.vue';
import ConfirmationForm from '../../components/form/member/ConfirmationForm.vue';
import UserInfoForm from '../../components/form/member/UserInfoForm.vue';
import ServiceAgreeForm from '../../components/form/ServiceAgreeForm.vue';
import InputFormHeader from '../../components/utils/InputFormHeader.vue';

const router = useRouter();

// ページ(0:ユーザー情報 1:所属先情報 2:登録確認)
const page = ref(0);
// Stepperに表示するタイトル
const titleList = ['ユーザー情報', '所属先情報', '登録確認'];
// アラートダイアログフラグ
const isAlertFlg = ref(false);
// エラーメッセージ
const errMessage = ref('');
// MessageChip表示フラグ
const isMessageChip = ref(false);
// CautionDialog表示フラグ
const isDisplayCautionDialog = ref(true);
// 会員ID（メールアドレス）エラーフラグ
const isMailAddressErr = ref(false);

// 入力値
const inputData = reactive({
  user_name_sei: '',
  user_name_mei: '',
  mail_address: '',
  mail_address_conf: '',
  user_attribute: UserAttributeCode.default.REAGENT,
  work_school_name: '',
  affiliation_department_name: '',
  postal_cd: '',
  todohuken_name: '',
  street_address: '',
  phone_number: '',
});

// 利用規約・プライバシーポリシーチェック状態
const isAgreeChecked = reactive({
  terms: false,
  policy: false,
});

/**
 * エラーチップを初期化
 */
const resetError = async () => {
  errMessage.value = '';
  isMessageChip.value = false;
};

/**
 * ユーザー情報チェック
 */
const checkUserInfo = async () => {
  const formData = new FormData();
  formData.append('mail_address', inputData.mail_address_conf);

  // ユーザー情報チェック
  await axios
    .post('/cmr001001/check-user', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          isMessageChip.value = false;
          errMessage.value = '';
          isMailAddressErr.value = false;
          // 所属先情報入力画面を表示
          page.value = 1;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.DUPLICATE_EMAIL_ERROR:
            case DetailResultStatusCode.BAN_DOMAIN_ERROR:
            case DetailResultStatusCode.PARAMETER_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              isMailAddressErr.value = true;
              break;

            // システムエラーの場合
            case DetailResultStatusCode.SYSTEM_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch(() => {
      // エラー画面に遷移
      router.push({ name: 'error' });
    });
};

/**
 * 会員登録
 */
const registerMember = async () => {
  // Loading Start
  Loading.show();
  // 登録データ作成
  const formData = new FormData();
  formData.append('user_name_sei', inputData.user_name_sei);
  formData.append('user_name_mei', inputData.user_name_mei);
  formData.append('mail_address', inputData.mail_address);
  formData.append('mail_address_conf', inputData.mail_address_conf);
  formData.append('work_school_name', inputData.work_school_name);
  formData.append('affiliation_department_name', inputData.affiliation_department_name);
  formData.append('postal_cd', inputData.postal_cd);
  formData.append('todohuken_name', inputData.todohuken_name);
  formData.append('street_address', inputData.street_address);
  formData.append('phone_number', inputData.phone_number);
  formData.append('user_attribute', inputData.user_attribute);

  // 会員登録
  await axios
    .post('/cmr001002/new-complete', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // ダイアログ表示
          isAlertFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.BAN_DOMAIN_ERROR:
            case DetailResultStatusCode.DUPLICATE_EMAIL_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              break;

            // システムエラーの場合
            case DetailResultStatusCode.PARAMETER_ERROR:
            case DetailResultStatusCode.SYSTEM_ERROR:
            case DetailResultStatusCode.DELETE_ERROR:
            case DetailResultStatusCode.INSERT_ERROR:
              router.push({ name: 'error' });
              break;

            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch(() => {
      // エラー画面に遷移
      router.push({ name: 'error' });
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};
</script>

<style scoped>
.cmr00100-form-area {
  width: 80%;
  max-width: 800px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
}

.q-btn:disabled {
  background-color: var(--q-opacityDisable) !important;
  border-color: var(--q-opacityDisable) !important;
  color: #fff !important;
}
</style>
<style>
.cmr00100-form-area .q-chip {
  width: 600px;
  margin: 0 auto 10px;
}
</style>
